import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { Botao } from 'src/componentes'
import { Breadcrumb } from 'src/componentes/breadcrumb'
import { Cabecalho } from 'src/componentes/cabecalho'
import { formatarMoeda } from 'src/componentes/funcoes/moeda'
import { Cores } from 'src/componentes/styles/cores'
import {
  Tabela,
  TabelaDados,
  TabelaRef,
  TabelaResposta
} from 'src/componentes/tabela'
import {
  ExecutionItemResponse,
  ExecutionItemResult,
  ExecutionResponse,
  EXECUTION_ITEM_RESULT_TEMA
} from 'src/dto'
import { formatadores } from 'src/paginas/aluno/painel-financeiro/pagina-financeiro/formatadores'
import { RotasBackgroundServices } from 'src/rotas'
import { Api } from 'src/servicos'

import { BadgeEstado, MensagemErro } from './componentes'
import {
  Aviso,
  colunaNumericaStyle,
  CampoBusca,
  Dados,
  ResumoExecucao,
  ResumoItem,
  Select,
  Toolbar,
  ToolbarAcoes,
  ToolbarTitulo
} from './styles'
import { formatarParcela, montarUrlCloudWatch } from './utils'

interface ItensLocationState {
  execucao?: ExecutionResponse
  serviceName?: string
}

export const PaginaItensBackgroundServices: React.FC = () => {
  const tabelaRef = useRef<TabelaRef>()
  const primeiraRender = useRef(true)
  const history = useHistory()
  const location = useLocation<ItensLocationState>()
  const { executionId } = useParams<{ executionId: string }>()

  const execucao = location.state?.execucao
  const serviceName = location.state?.serviceName

  // Apenas estes services preenchem installmentNumber/amount/paymentMethod nos itens.
  // Todos os demais operam em granularidade de matrícula ou não registram itens financeiros.
  const SERVICES_COM_PARCELA = new Set([
    'RealizarProcessamentoCartaoCreditoBackgroundService',
    'RealizarProcessamentoCartaoCreditoRecorrenciaBackgroundService',
    'RealizarProcessamentoCartaoCreditoRecorrenciaBraspagBackgroundService',
    'TratarSituacaoPagamentoBackgroundService',
    'RealizarProcessamentoCartaoCreditoCarrinhoCompraBackgroundService'
  ])
  const exibirColunasParcela = !!serviceName && SERVICES_COM_PARCELA.has(serviceName)

  const [filtroResult, definirFiltroResult] = useState<
    ExecutionItemResult | ''
  >('')
  // Texto digitado x valor aplicado (debounced) para não recarregar a cada tecla.
  const [buscaNome, definirBuscaNome] = useState('')
  const [filtroNome, definirFiltroNome] = useState('')
  // Suspect: execução achou itens mas a lista veio vazia (1ª página, sem filtro).
  const [listaVaziaInesperada, definirListaVaziaInesperada] = useState(false)

  // Debounce do nome: aplica o filtro 400ms após a última tecla.
  useEffect(() => {
    const id = setTimeout(() => definirFiltroNome(buscaNome.trim()), 400)
    return () => clearTimeout(id)
  }, [buscaNome])

  // A Tabela já carrega no mount; só recarregamos quando algum filtro mudar.
  useEffect(() => {
    if (primeiraRender.current) {
      primeiraRender.current = false
      return
    }
    tabelaRef.current?.IrParaPagina()
    tabelaRef.current?.CarregarDados()
  }, [filtroResult, filtroNome])

  const obterDados = async (
    pagina: number,
    tamanhoPagina: number
  ): Promise<TabelaResposta | null> => {
    const dados = await Api.ObterItensExecucao(
      executionId,
      filtroResult || undefined,
      filtroNome || undefined,
      pagina,
      tamanhoPagina
    )

    if (!dados) return null

    const semItens = dados.itens.length === 0
    definirListaVaziaInesperada(
      semItens &&
        pagina === 1 &&
        !filtroResult &&
        !filtroNome &&
        !!execucao &&
        execucao.totalFound > 0
    )

    return {
      Dados: dados.itens,
      Paginacao: {
        Pagina: pagina,
        TotalRegistros: dados.quantidade
      }
    } as TabelaResposta
  }

  const voltar = () => {
    if (serviceName) {
      history.push(
        `${RotasBackgroundServices.Execucoes}/${encodeURIComponent(
          serviceName
        )}`
      )
      return
    }
    history.push(RotasBackgroundServices.Resumo)
  }

  return (
    <>
      <Cabecalho>
        <Breadcrumb
          titulo="Detalhe da execução"
          atalhos={[
            {
              texto: 'Background Services',
              acao: () => history.push(RotasBackgroundServices.Resumo)
            },
            ...(serviceName
              ? [
                  {
                    texto: serviceName,
                    acao: voltar
                  }
                ]
              : []),
            { texto: 'Detalhe da execução' }
          ]}
        />
      </Cabecalho>

      <ResumoExecucao>
        {serviceName && (
          <ResumoItem>
            <div className="chave">Serviço</div>
            <div className="valor">{serviceName}</div>
          </ResumoItem>
        )}
        {execucao && (
          <>
            <ResumoItem>
              <div className="chave">Execução</div>
              <div className="valor">
                {formatadores.dataHoraLocaleString(execucao.startedAt)}
              </div>
            </ResumoItem>
            <ResumoItem>
              <div className="chave">Encontradas</div>
              <div className="valor">{execucao.totalFound}</div>
            </ResumoItem>
            <ResumoItem>
              <div className="chave">Sucesso</div>
              <div className="valor" style={{ color: Cores.SUCESSO }}>
                {execucao.totalSucceeded}
              </div>
            </ResumoItem>
            <ResumoItem>
              <div className="chave">Erro</div>
              <div className="valor" style={{ color: Cores.PERIGO_ERRO }}>
                {execucao.totalFailed}
              </div>
            </ResumoItem>
            <ResumoItem>
              <div className="chave">Puladas</div>
              <div className="valor">{execucao.totalSkipped}</div>
            </ResumoItem>
          </>
        )}
        <ResumoItem>
          <div className="chave">ExecutionId</div>
          <div className="valor" style={{ fontSize: 12 }}>
            {executionId}
          </div>
        </ResumoItem>
      </ResumoExecucao>

      {listaVaziaInesperada && (
        <Aviso>
          A execução encontrou {execucao?.totalFound} itens, mas nenhum foi
          processado/registrado. Verifique o log da execução pelo ExecutionId{' '}
          <strong>{executionId}</strong>.
        </Aviso>
      )}

      <Toolbar>
        <ToolbarTitulo>
          {exibirColunasParcela ? 'Parcelas processadas' : 'Matrículas processadas'}
        </ToolbarTitulo>
        <ToolbarAcoes>
          <CampoBusca
            type="text"
            value={buscaNome}
            placeholder="Buscar por aluno"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              definirBuscaNome(e.target.value)
            }
          />
          <Select
            value={filtroResult}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              definirFiltroResult(e.target.value as ExecutionItemResult | '')
            }
          >
            <option value="">Resultado: Todos</option>
            <option value={ExecutionItemResult.Success}>
              {EXECUTION_ITEM_RESULT_TEMA[ExecutionItemResult.Success].label}
            </option>
            <option value={ExecutionItemResult.Error}>
              {EXECUTION_ITEM_RESULT_TEMA[ExecutionItemResult.Error].label}
            </option>
            <option value={ExecutionItemResult.Skipped}>
              {EXECUTION_ITEM_RESULT_TEMA[ExecutionItemResult.Skipped].label}
            </option>
          </Select>
          <Botao
            type="button"
            texto="Ver no CloudWatch"
            tema="Secundario"
            onClick={() =>
              window.open(
                montarUrlCloudWatch(executionId, true),
                '_blank',
                'noopener'
              )
            }
          />
          <Botao type="button" texto="Voltar" tema="Link" onClick={voltar} />
        </ToolbarAcoes>
      </Toolbar>

      <Dados>
        <Tabela
          ref={tabelaRef}
          Colunas={[
            {
              Chave: 'studentName',
              RenderizarCabecalho: () => (
                <div style={{ width: 220 }}>Aluno</div>
              ),
              Renderizar: (linha: TabelaDados) => {
                const item = linha as ExecutionItemResponse
                return (
                  <div>
                    <span>{item.studentName ?? '—'}</span>
                    {item.errorMessage && (
                      <MensagemErro
                        mensagem={item.errorMessage}
                        tipo={
                          item.result === ExecutionItemResult.Error
                            ? 'erro'
                            : 'alerta'
                        }
                      />
                    )}
                  </div>
                )
              }
            },
            ...(exibirColunasParcela
              ? [
                  {
                    Chave: 'installmentNumber',
                    Alinhamento: 'center' as const,
                    StyleColuna: colunaNumericaStyle,
                    RenderizarCabecalho: () => <div>Parcela</div>,
                    Renderizar: (linha: TabelaDados) => (
                      <>{formatarParcela(linha.installmentNumber)}</>
                    )
                  },
                  {
                    Chave: 'amount',
                    StyleColuna: colunaNumericaStyle,
                    RenderizarCabecalho: () => <div>Valor</div>,
                    Renderizar: (linha: TabelaDados) => (
                      <>
                        {linha.amount !== null && linha.amount !== undefined
                          ? formatarMoeda(linha.amount)
                          : '—'}
                      </>
                    )
                  },
                  {
                    Chave: 'paymentMethod',
                    RenderizarCabecalho: () => <div>Forma</div>,
                    Renderizar: (linha: TabelaDados) => (
                      <>{linha.paymentMethod ?? '—'}</>
                    )
                  }
                ]
              : []),
            {
              Chave: 'result',
              RenderizarCabecalho: () => (
                <div style={{ width: 120 }}>Resultado</div>
              ),
              Renderizar: (linha: TabelaDados) => (
                <BadgeEstado
                  tema={
                    EXECUTION_ITEM_RESULT_TEMA[
                      (linha as ExecutionItemResponse).result
                    ]
                  }
                />
              )
            },
            {
              Chave: 'processedAt',
              RenderizarCabecalho: () => (
                <div style={{ width: 150 }}>Quando</div>
              ),
              Renderizar: (linha: TabelaDados) => (
                <span>
                  {formatadores.dataHoraLocaleString(linha.processedAt)}
                </span>
              )
            }
          ]}
          ObterDados={obterDados}
          ComPaginacao
          TamanhoPagina={50}
          TextoSemResultados={
            exibirColunasParcela
              ? 'Nenhuma parcela para este filtro.'
              : 'Nenhuma matrícula para este filtro.'
          }
        />
      </Dados>
    </>
  )
}

export default PaginaItensBackgroundServices
